import { Circle, Path, Rect } from '@react-pdf/renderer';
import { Building, Calendar, Clock, Laptop, type IconNode, type LucideIcon } from 'lucide-react';
import { themeColor } from '../../styles';

const STROKE_PROPS = {
  stroke: themeColor('teal-600'),
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
};

/** lucide-react emits DOM `<svg>`; react-pdf needs its own Svg/Path primitives. */
export const LUCIDE_ICON_NODES = new Map<LucideIcon, IconNode>([
  [
    Calendar,
    [
      ['path', { d: 'M8 2v4' }],
      ['path', { d: 'M16 2v4' }],
      ['rect', { width: '18', height: '18', x: '3', y: '4', rx: '2' }],
      ['path', { d: 'M3 10h18' }],
    ],
  ],
  [
    Clock,
    [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M12 6v6l4 2' }],
    ],
  ],
  [
    Building,
    [
      ['path', { d: 'M12 10h.01' }],
      ['path', { d: 'M12 14h.01' }],
      ['path', { d: 'M12 6h.01' }],
      ['path', { d: 'M16 10h.01' }],
      ['path', { d: 'M16 14h.01' }],
      ['path', { d: 'M16 6h.01' }],
      ['path', { d: 'M8 10h.01' }],
      ['path', { d: 'M8 14h.01' }],
      ['path', { d: 'M8 6h.01' }],
      ['path', { d: 'M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3' }],
      ['rect', { x: '4', y: '2', width: '16', height: '20', rx: '2' }],
    ],
  ],
  [
    Laptop,
    [
      [
        'path',
        {
          d: 'M18 5a2 2 0 0 1 2 2v8.526a2 2 0 0 0 .212.897l1.068 2.127a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45l1.068-2.127A2 2 0 0 0 4 15.526V7a2 2 0 0 1 2-2z',
        },
      ],
      ['path', { d: 'M20.054 15.987H3.946' }],
    ],
  ],
]);

const toNumber = (value: string | undefined): number => Number(value ?? 0);

export const renderLucideNode = (
  node: IconNode[number],
  index: number
): React.ReactElement | null => {
  const [tag, attrs] = node;

  if (tag === 'path') {
    return <Path key={index} d={attrs.d} {...STROKE_PROPS} />;
  }

  if (tag === 'circle') {
    return (
      <Circle
        key={index}
        cx={toNumber(attrs.cx)}
        cy={toNumber(attrs.cy)}
        r={toNumber(attrs.r)}
        {...STROKE_PROPS}
      />
    );
  }

  if (tag === 'rect') {
    return (
      <Rect
        key={index}
        x={toNumber(attrs.x)}
        y={toNumber(attrs.y)}
        width={toNumber(attrs.width)}
        height={toNumber(attrs.height)}
        rx={toNumber(attrs.rx)}
        {...STROKE_PROPS}
      />
    );
  }

  return null;
};
